import type { StudentBillingReportRow, StudentBillingSessionRow } from '../types/student';
import { shouldShowBillingPerSessionPrice } from './lessonBillingPrices';

const STUDENT_BILLING_CSV_HEADERS_COMMON = [
  'studentId',
  'firstName',
  'lastName',
  'lessonName',
  'lessonId',
  'lessonType',
  'termName',
  'periodStart',
  'periodEnd',
  'bookingStatus',
  'paymentStatus',
  'sessionsAttended',
] as const;

const STUDENT_BILLING_CSV_HEADERS_MIDDLE = [
  'sessionsTotal',
  'enrollmentSessionsTotal',
  'sessionsMissed',
  'sessionsCancelled',
  'perSessionPrice',
  'fullPrice',
] as const;

function billingBasisLabel(basis: StudentBillingReportRow['billingBasis']): string {
  switch (basis) {
    case 'attendance_in_period':
      return 'Attendance in period';
    case 'term_full_price_due_in_period':
      return 'Term full price due in period';
    case 'term_full_price_outside_period':
      return 'Term full price outside period';
    case 'enrollment_total':
      return 'Enrollment total';
    case 'missing_session_price':
      return '';
    default:
      return '';
  }
}

export type StudentBillingCsvExportOptions = {
  /**
   * When true, exports bill-by-attendance layout:
   * - Adds `attendedDates` (semicolon-separated YYYY-MM-DD values).
   * - Omits `billingBasis` and `totalDue` (period due is the billable amount).
   * - Places `camp_booking_type` before `periodTotalDue`.
   *
   * When false (default), exports the legacy layout with `billingBasis` and `totalDue`.
   */
  billByAttendance?: boolean;
};

export function getStudentBillingCsvHeaders(
  options?: StudentBillingCsvExportOptions
): readonly string[] {
  const headers: string[] = [...STUDENT_BILLING_CSV_HEADERS_COMMON];
  if (options?.billByAttendance) {
    headers.push('attendedDates');
  }
  headers.push(...STUDENT_BILLING_CSV_HEADERS_MIDDLE);
  if (options?.billByAttendance) {
    headers.push('camp_booking_type', 'periodTotalDue');
  } else {
    headers.push('periodTotalDue', 'billingBasis', 'camp_booking_type', 'totalDue');
  }
  return headers;
}

function formatAttendedDatesForCsv(dates?: string[]): string {
  if (!dates?.length) return '';
  return [...dates].sort().join('; ');
}

function escapeCsvCell(value: unknown): string {
  const s = String(value ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function buildStudentBillingCsvRows(
  rows: StudentBillingReportRow[],
  options?: StudentBillingCsvExportOptions
): Record<string, string | number | boolean | undefined>[] {
  const billByAttendance = options?.billByAttendance === true;
  return rows.map((r) => {
    const campBookingType =
      r.lessonType === 'camp' && r.campBookingType
        ? r.campBookingType === 'daily'
          ? 'daily'
          : 'full_camp'
        : '';

    const row: Record<string, string | number | boolean | undefined> = {
      studentId: r.studentId,
      firstName: r.firstName,
      lastName: r.lastName,
      lessonName: r.lessonName,
      lessonId: r.lessonId,
      lessonType: r.lessonType,
      termName: r.termName ?? '',
      periodStart: r.periodStart ?? '',
      periodEnd: r.periodEnd ?? '',
      bookingStatus: r.bookingStatus,
      paymentStatus: r.paymentStatus,
      sessionsAttended: r.sessionsAttended,
      ...(billByAttendance
        ? { attendedDates: formatAttendedDatesForCsv(r.attendedDates) }
        : {}),
      sessionsTotal: r.sessionsTotal,
      enrollmentSessionsTotal: r.enrollmentSessionsTotal ?? r.sessionsTotal,
      sessionsMissed: r.sessionsMissed,
      sessionsCancelled: r.sessionsCancelled,
      perSessionPrice: shouldShowBillingPerSessionPrice(r) ? r.sessionPrice ?? r.perSessionPrice : '',
      fullPrice: r.fullPrice,
      camp_booking_type: campBookingType,
    };

    if (billByAttendance) {
      row.periodTotalDue =
        r.billingBasis === 'missing_session_price' ? '' : r.periodTotalDue ?? '';
    } else {
      row.periodTotalDue =
        r.billingBasis === 'missing_session_price' ? '' : r.periodTotalDue ?? r.totalDue;
      row.billingBasis = billingBasisLabel(r.billingBasis);
      row.totalDue = r.totalDue;
    }

    return row;
  });
}

export function buildStudentBillingDrilldownCsvRows(
  rows: StudentBillingSessionRow[]
): Record<string, string | number | boolean | undefined>[] {
  return rows.map((r) => ({
    sessionDate: r.sessionDate,
    status: r.status,
    price: r.price,
    notes: r.notes ?? '',
  }));
}

export function buildStudentBillingCsvFilename(
  periodStart?: string,
  periodEnd?: string,
  exportedAt: Date = new Date()
): string {
  const periodSuffix =
    periodStart && periodEnd ? `${periodStart}_to_${periodEnd}` : 'all';
  return `student-billing_${periodSuffix}_${exportedAt.toISOString().split('T')[0]}.csv`;
}

export function stringifyCsvRows(
  rows: Record<string, string | number | boolean | undefined>[],
  options?: { headers?: readonly string[] }
): string {
  const headers =
    options?.headers ??
    (rows.length ? Object.keys(rows[0]!) : [...getStudentBillingCsvHeaders()]);
  const lines = [headers.map(escapeCsvCell).join(',')];
  for (const row of rows) {
    lines.push(headers.map((header) => escapeCsvCell(row[header])).join(','));
  }
  return lines.join('\n');
}
